/**
 * §7.34 Pandemic insurance trigger forecaster — Phase 11+ feature wave.
 *
 * Pandemic insurance is a parametric construct: payout fires when public,
 * pre-agreed criteria are met (WHO declaration; case-count + geographic
 * spread thresholds; mortality multipliers). Finance ministers need to
 * see — BEFORE the wave hits — when each instrument is likely to trigger
 * so they can pre-position fiscal response. The §7.25 Minister Pack picks
 * up the pinned trigger forecast as a Finance-persona indicator.
 *
 * ## Why a separate surface
 *
 * §7.26 PHEIC Predictor already gives the binary "will WHO declare?"
 * forecast. The gap this surface fills: many parametric instruments use
 * additional criteria (death counts; country-spread; growth phase) that
 * fire AFTER PHEIC and on different curves. Operators routinely
 * misestimate WHEN payout actually arrives — PEF Class B for COVID-19
 * paid 9 weeks after PHEIC declaration, far too late for the surge
 * fiscal-capacity gap the instrument was supposed to fill (see
 * Stein & Sridhar 2017 Lancet critique; Glassman 2020 CGD).
 *
 * ## Modelled instruments (all public)
 *
 *   - WB PEF 2017-2020 IBRD Class A — first-loss tranche. Triggers:
 *     - Deaths ≥ 250 in any one country
 *     - Spread to ≥ 2 countries (international)
 *     - "Growth phase" certification (rolling 7d growth > 0)
 *     - Time elapsed ≥ 84 days from outbreak start (eligibility window)
 *     Payout: ~USD 195m (insurance + cat bond aggregate; original 2017
 *     issuance). Source: World Bank, "Pandemic Emergency Financing
 *     Facility: Frequently Asked Questions" + IBRD CAT-DDO documentation.
 *
 *   - WB PEF 2017-2020 IBRD Class B — second-loss tranche, higher payout.
 *     Triggers: as Class A but with HIGHER death + spread thresholds:
 *     - Deaths ≥ 2500 in 2+ countries (one ≥ 250)
 *     - Growth-phase certification
 *     Payout: ~USD 95m (cat bond aggregate). Source: same as Class A.
 *
 *   - Pandemic Fund (2022+) — successor structured fund administered by
 *     the World Bank Financial Intermediary Fund. Trigger criteria are
 *     LESS algorithmic (multilateral request + WHO technical assessment)
 *     than PEF was; modelled here as a soft trigger fired by PHEIC plus
 *     a regional-burden threshold. Payouts are project-based grants, NOT
 *     parametric, so the "magnitude" column is "request-based · undisclosed
 *     ceiling". Source: G20 Pandemic Fund concept note (2022) + WB FIF
 *     governance documentation.
 *
 *   - African Risk Capacity (ARC) Outbreak & Epidemic insurance — regional
 *     parametric instrument for African Union member states. Triggers based
 *     on case-count thresholds per pathogen-class (Ebola, Marburg, meningitis,
 *     Lassa). Modelled as a region-conditional trigger (fires only when the
 *     destination region is an AU member). Source: ARC "Outbreak & Epidemic
 *     Insurance Product Note" 2020.
 *
 *   - Re-insurance cat-bond reference tier — a representative private-market
 *     instrument modelled on the IBRD 2017 cat-bond + reinsurance-syndicate
 *     COVID-19 issuances reviewed in S&P Global Ratings 2021 + Munich Re
 *     2021 annual report. Trigger: WHO PHEIC declaration + 84-day eligibility
 *     period + minimum mortality threshold. Used as a "what the private
 *     reinsurance market would pay" reference rate. Payouts are public for
 *     the IBRD 2017 issuance ($425m total covered, ~$95m paid for COVID);
 *     subsequent issuances are private.
 *
 * ## Honesty rail
 *
 * The PEF 2017-20 has been widely criticised for paying out too late for
 * COVID-19 (Stein & Sridhar 2017 Lancet — written before COVID, predicted
 * the failure mode; Brim & Wenham 2019 BMJ Glob Health; Glassman 2020 CGD).
 * The instruments are calibrated for fast-burn outbreaks like Ebola, where
 * the 250-death threshold + 84-day eligibility window correlate well with
 * surge-need timing. Slow-burn respiratory pandemics (COVID, novel flu)
 * cross those thresholds AFTER the optimal pre-positioning window. The
 * honesty banner makes this explicit; the surface must NOT pretend
 * parametric insurance is a substitute for an unconditional surge facility
 * (e.g., expanded IMF SDR allocation, central-bank lender-of-last-resort).
 *
 * ## Model
 *
 *   For each instrument I:
 *     - For each criterion c_i in I:
 *       - Compute current "% to trigger" = current_state / threshold
 *       - Compute projected day-to-trigger by walking the SEIRD curve
 *         (deaths cumulative, growth-rate derivative for the growth-phase
 *         certification, spread approximated by import-rate × time)
 *     - Instrument fires on day = max(criterion-fire-days) (AND logic)
 *     - Days-from-now = projected_fire_day - 0 (we start at outbreak day 0)
 *
 * Spread (country count) is approximated rather than simulated — we use
 * the empirical curve: COVID-2020 reached 100+ countries by day ~90;
 * Ebola-2014 reached 5+ countries by day ~180; SARS-2003 reached 30+
 * countries by day ~60. We bucket pathogens by transmission-class (high
 * R0 respiratory → COVID-curve; moderate-R0 respiratory → SARS-curve;
 * low-R0 hemorrhagic → Ebola-curve) and use that empirical spread
 * trajectory as the modelled curve.
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { simulateSeird } from '@biomax/plugin-epidemiology';
import { Sparkline } from '../sparkline';
import { useScenario } from '../ScenarioContext';
import { PinToBriefButton } from '../pack/PinToBriefButton';
import { SurfaceScaffold } from '../SurfaceScaffold';
import { HonestyRail } from '../HonestyRail';

/** AU member states (ISO3) — used for the ARC Outbreak & Epidemic
 *  Insurance regional-conditional trigger. Source: African Union member
 *  list (au.int). Subset relevant to the region database. */
const AU_MEMBER_ISO3 = new Set<string>([
  'DZA', 'AGO', 'BEN', 'BWA', 'BFA', 'BDI', 'CMR', 'CPV', 'CAF', 'TCD',
  'COM', 'COG', 'COD', 'CIV', 'DJI', 'EGY', 'GNQ', 'ERI', 'SWZ', 'ETH',
  'GAB', 'GMB', 'GHA', 'GIN', 'GNB', 'KEN', 'LSO', 'LBR', 'LBY', 'MDG',
  'MWI', 'MLI', 'MRT', 'MUS', 'MAR', 'MOZ', 'NAM', 'NER', 'NGA', 'RWA',
  'STP', 'SEN', 'SYC', 'SLE', 'SOM', 'ZAF', 'SSD', 'SDN', 'TZA', 'TGO',
  'TUN', 'UGA', 'ZMB', 'ZWE',
]);

/** Empirical country-spread trajectories anchored to past outbreaks.
 *  Returns the cumulative country count by day-since-outbreak-start.
 *  Pathogen → trajectory by R0 + transmission-class bucket. */
type SpreadClass = 'covid-like' | 'sars-like' | 'flu-pandemic' | 'ebola-like' | 'slow-zoonotic';

function classifySpread(R0: number, infectiousPeriodDays: number): SpreadClass {
  if (R0 >= 2.0 && infectiousPeriodDays >= 5) return 'covid-like';
  if (R0 >= 2.0 && infectiousPeriodDays < 5) return 'flu-pandemic';
  if (R0 >= 1.3 && R0 < 2.0) return 'sars-like';
  if (R0 >= 1.0) return 'ebola-like';
  return 'slow-zoonotic';
}

/** Empirical (day, countries) anchors for each class.
 *  Source attribution per class given inline (these are observed-curve
 *  approximations from the AAR library and WHO situation reports). */
const SPREAD_CURVES: Record<SpreadClass, { points: readonly [number, number][]; source: string }> = {
  'covid-like': {
    // COVID-2020 WHO SITREP-3 (Jan 23 2020): 5 countries by ~day 25
    // SITREP-30 (Feb 19 2020): 25 by day 50
    // SITREP-60 (Mar 19 2020): 173 by day 80
    points: [[0, 1], [14, 1], [25, 5], [50, 25], [80, 173], [120, 200]],
    source: 'WHO COVID-19 SITREP-3/30/60 (Jan-Mar 2020)',
  },
  'flu-pandemic': {
    // H1N1-2009: PHEIC day 25; spread to 74 countries by day 60.
    // WHO H1N1 pandemic update timeline.
    points: [[0, 1], [25, 9], [40, 30], [60, 74], [120, 168]],
    source: 'WHO H1N1 pandemic update timeline (Apr-Jul 2009)',
  },
  'sars-like': {
    // SARS-2003: spread to 30 countries by day 90.
    // WHO SARS chronology.
    points: [[0, 1], [30, 5], [60, 17], [90, 30], [180, 32]],
    source: 'WHO SARS chronology (Nov 2002 - Jul 2003)',
  },
  'ebola-like': {
    // Ebola-2014 (West Africa): 3 countries day 60; 6 by day 180; 8 cumulative
    // by end of outbreak (with international exports). WHO Ebola SITREPs.
    points: [[0, 1], [60, 3], [120, 4], [180, 6], [365, 8]],
    source: 'WHO Ebola SITREP series 2014-2016',
  },
  'slow-zoonotic': {
    // MERS-like: rare cross-border spread over years; conservative anchor
    // for low-R0 zoonotics.
    points: [[0, 1], [180, 2], [365, 4], [730, 8]],
    source: 'WHO MERS-CoV monthly updates 2012-2024',
  },
};

/** Lookup cumulative countries-affected at day t by linear interpolation. */
function countriesAtDay(curve: { points: readonly [number, number][] }, t: number): number {
  const pts = curve.points;
  if (t <= pts[0]![0]) return pts[0]![1];
  if (t >= pts[pts.length - 1]![0]) return pts[pts.length - 1]![1];
  for (let i = 1; i < pts.length; i++) {
    const [d0, c0] = pts[i - 1]!;
    const [d1, c1] = pts[i]!;
    if (t >= d0 && t <= d1) {
      const frac = (t - d0) / Math.max(1, d1 - d0);
      return c0 + (c1 - c0) * frac;
    }
  }
  return pts[pts.length - 1]![1];
}

/** Find the first day a metric series crosses a threshold; null if never. */
function firstDayCrossing(series: readonly number[], threshold: number): number | null {
  for (let t = 0; t < series.length; t++) {
    if (series[t]! >= threshold) return t;
  }
  return null;
}

/** Find the first day a country-spread curve crosses a threshold. */
function firstDayCountriesGte(curve: { points: readonly [number, number][] }, threshold: number, horizon: number): number | null {
  for (let t = 0; t < horizon; t++) {
    if (countriesAtDay(curve, t) >= threshold) return t;
  }
  return null;
}

type CriterionResult = {
  label: string;
  currentValue: number | string;
  threshold: number | string;
  pctToTrigger: number; // 0-100; >=100 means fired
  fireDay: number | null;
};

type InstrumentResult = {
  instrument: Instrument;
  criteriaResults: readonly CriterionResult[];
  /** All-criteria-fire day. null if any criterion never fires in horizon. */
  triggerFireDay: number | null;
  /** Days from PHEIC declaration to payout, where PEF/cat-bond has the
   *  documented post-trigger waiting period. */
  daysFromTriggerToPayout: number;
  regionEligible: boolean;
  ineligibilityReason?: string;
};

interface Instrument {
  id: string;
  shortLabel: string;
  fullLabel: string;
  payoutLabel: string;
  payoutAnchor: string;
  postTriggerDelayDays: number;
  source: string;
  regional: 'global' | 'au-member' | 'low-income';
  /** AND-composed criteria; instrument fires when ALL fire. */
  criteria: readonly {
    id: string;
    label: string;
    type: 'cumulative-deaths' | 'cumulative-deaths-multi-country' | 'countries-affected' | 'growth-phase' | 'who-pheic' | 'days-elapsed' | 'multilateral-request';
    threshold: number;
  }[];
}

/** Modelled instruments. All criteria are from public documentation. */
const INSTRUMENTS: readonly Instrument[] = [
  {
    id: 'wb-pef-class-a',
    shortLabel: 'WB PEF Class A',
    fullLabel: 'World Bank Pandemic Emergency Financing Facility (2017-2020) — IBRD Insurance Window Class A',
    payoutLabel: 'USD ~50-195m',
    payoutAnchor: 'IBRD 2017 issuance: USD 425m total capacity; ~USD 195m insurance + ~USD 230m cat bond; Class A first-loss tranche payout ~USD 50m per documented filing',
    postTriggerDelayDays: 63, // PEF documented 9-week post-trigger delay (Stein 2017 + WB FAQ)
    source: 'World Bank PEF FAQ (2017); Stein F, Sridhar D, Lancet 2017 PMID 28527693',
    regional: 'global',
    criteria: [
      { id: 'deaths', label: 'Cumulative deaths in one country', type: 'cumulative-deaths', threshold: 250 },
      { id: 'countries', label: 'Countries affected (cross-border spread)', type: 'countries-affected', threshold: 2 },
      { id: 'growth', label: 'Growth-phase certification (7d growth > 0)', type: 'growth-phase', threshold: 0 },
      { id: 'elapsed', label: 'Days from outbreak start (eligibility window)', type: 'days-elapsed', threshold: 84 },
    ],
  },
  {
    id: 'wb-pef-class-b',
    shortLabel: 'WB PEF Class B',
    fullLabel: 'World Bank Pandemic Emergency Financing Facility (2017-2020) — IBRD Cat Bond Class B',
    payoutLabel: 'USD ~95m',
    payoutAnchor: 'IBRD 2017 cat bond Class B tranche; ~USD 95m payout fired for COVID-19 in April 2020 (~9 weeks post-trigger)',
    postTriggerDelayDays: 63,
    source: 'World Bank PEF FAQ (2017); WB Class B cat bond prospectus 2017',
    regional: 'global',
    criteria: [
      { id: 'deaths', label: 'Cumulative deaths in 2+ countries (one ≥ 2500)', type: 'cumulative-deaths', threshold: 2500 },
      { id: 'countries', label: 'Countries affected (2+ with ≥250 deaths)', type: 'countries-affected', threshold: 2 },
      { id: 'growth', label: 'Growth-phase certification (7d growth > 0)', type: 'growth-phase', threshold: 0 },
      { id: 'elapsed', label: 'Days from outbreak start (eligibility window)', type: 'days-elapsed', threshold: 84 },
    ],
  },
  {
    id: 'pandemic-fund',
    shortLabel: 'Pandemic Fund (2022+)',
    fullLabel: 'Pandemic Fund (Financial Intermediary Fund, World Bank, 2022+)',
    payoutLabel: 'Project-based grants (undisclosed ceiling)',
    payoutAnchor: 'Initial capitalisation ~USD 2bn (2022); call-based grant disbursement, NOT parametric — magnitude not pre-set',
    postTriggerDelayDays: 90, // Estimate; multilateral request + WB grant cycle
    source: 'G20 Pandemic Fund Concept Note (2022); WB FIF Governance Framework',
    regional: 'global',
    criteria: [
      { id: 'who-pheic', label: 'WHO PHEIC declaration (or technical assessment)', type: 'who-pheic', threshold: 1 },
      { id: 'multilateral', label: 'Multilateral request from affected member state(s)', type: 'multilateral-request', threshold: 1 },
    ],
  },
  {
    id: 'arc-outbreak',
    shortLabel: 'ARC Outbreak Insurance',
    fullLabel: 'African Risk Capacity Outbreak & Epidemic Insurance (regional, AU member states)',
    payoutLabel: 'USD ~10-30m per peril',
    payoutAnchor: 'ARC Outbreak & Epidemic Insurance Product Note 2020; payout sized to country pool contribution + reinsurance layer',
    postTriggerDelayDays: 21, // ARC faster than WB instruments (3-week target)
    source: 'African Risk Capacity Group "Outbreak & Epidemic Insurance Product Note" 2020',
    regional: 'au-member',
    criteria: [
      { id: 'deaths', label: 'Cumulative deaths (regional pathogen-class threshold)', type: 'cumulative-deaths', threshold: 100 },
      { id: 'who-pheic', label: 'WHO regional alert or PHEIC declaration', type: 'who-pheic', threshold: 1 },
    ],
  },
  {
    id: 'reinsurance-catbond',
    shortLabel: 'Reinsurance Cat-Bond (reference)',
    fullLabel: 'Representative private-market pandemic cat bond (Munich Re / Swiss Re / SCOR syndicates)',
    payoutLabel: 'Tranche-dependent (publicly: USD 95m for IBRD 2017 issuance)',
    payoutAnchor: 'IBRD 2017 cat bond Class B paid USD 95m for COVID-19 (April 2020); subsequent private issuances undisclosed',
    postTriggerDelayDays: 84, // Standard 84-day eligibility period in offering circulars
    source: 'S&P Global Ratings Insurance Industry Outlook 2021; Munich Re 2021 Annual Report',
    regional: 'global',
    criteria: [
      { id: 'who-pheic', label: 'WHO PHEIC declaration (binary trigger)', type: 'who-pheic', threshold: 1 },
      { id: 'deaths', label: 'Cumulative deaths (modelled mortality threshold)', type: 'cumulative-deaths', threshold: 1000 },
      { id: 'elapsed', label: 'Eligibility period (84-day waiting period)', type: 'days-elapsed', threshold: 84 },
    ],
  },
];

export function PandemicInsuranceTriggers() {
  const pathogens = listPathogens();
  const regions = listRegions();
  const { pathogenId, regionId, setPathogenId, setRegionId } = useScenario();

  const [horizonDays] = useState<number>(180);
  /** Day operator estimates WHO would declare PHEIC for this scenario.
   *  Default 60d (median across the 10 declared PHEICs in §7.26 corpus).
   *  Cross-references §7.26 PHEIC Predictor — operator should sync. */
  const [pheicDeclarationDay, setPheicDeclarationDay] = useState<number>(60);

  const view = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    const region = findRegion(regionId);
    if (!pathogen || !region) return null;

    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    const beta = pathogen.R0.value * gamma;
    const ifr = pathogen.ifrByAgeBand[Math.floor(pathogen.ifrByAgeBand.length / 2)]?.ifr ?? 0.005;

    const epi = simulateSeird({
      N: region.population.total, I0: 500,
      beta, gamma, sigma, ifr,
      durationDays: horizonDays, dtDays: 1,
    });

    const D = epi.compartments.D!; // Cumulative deaths
    const I = epi.compartments.I!;

    // Daily death increment for growth-phase certification (7d rolling > 0)
    const dailyDeaths: number[] = [];
    for (let t = 1; t < D.length; t++) {
      dailyDeaths.push(Math.max(0, D[t]! - D[t - 1]!));
    }
    // Growth phase = first day with positive 7-day sum (essentially day ~0)
    const growthFireDay = (() => {
      for (let t = 7; t < dailyDeaths.length; t++) {
        const sum = dailyDeaths.slice(t - 7, t).reduce((a, b) => a + b, 0);
        if (sum > 0) return t;
      }
      return null;
    })();

    const spreadClass = classifySpread(pathogen.R0.value, pathogen.infectiousPeriodDays.value);
    const spreadCurve = SPREAD_CURVES[spreadClass];

    // Region eligibility map — AU instrument needs an AU member.
    const iso3 = region.id.toUpperCase();
    const isAuMember = AU_MEMBER_ISO3.has(iso3);

    const instrumentResults: InstrumentResult[] = INSTRUMENTS.map(inst => {
      let regionEligible = true;
      let ineligibilityReason: string | undefined;
      if (inst.regional === 'au-member' && !isAuMember) {
        regionEligible = false;
        ineligibilityReason = 'Region is not an AU member state — ARC instrument does not apply';
      }

      const criteriaResults: CriterionResult[] = inst.criteria.map(c => {
        switch (c.type) {
          case 'cumulative-deaths':
          case 'cumulative-deaths-multi-country': {
            const currentDeaths = D[0] ?? 0;
            const fireDay = firstDayCrossing(D, c.threshold);
            const pct = Math.min(100, (currentDeaths / c.threshold) * 100);
            return {
              label: c.label,
              currentValue: Math.round(currentDeaths),
              threshold: c.threshold,
              pctToTrigger: pct,
              fireDay,
            };
          }
          case 'countries-affected': {
            const currentCountries = countriesAtDay(spreadCurve, 0);
            const fireDay = firstDayCountriesGte(spreadCurve, c.threshold, horizonDays);
            const pct = Math.min(100, (currentCountries / c.threshold) * 100);
            return {
              label: c.label,
              currentValue: Math.round(currentCountries),
              threshold: c.threshold,
              pctToTrigger: pct,
              fireDay,
            };
          }
          case 'growth-phase': {
            return {
              label: c.label,
              currentValue: growthFireDay !== null && growthFireDay <= 0 ? 'in growth' : 'pre-growth',
              threshold: '7d growth > 0',
              pctToTrigger: growthFireDay !== null ? 100 : 0,
              fireDay: growthFireDay,
            };
          }
          case 'who-pheic': {
            return {
              label: c.label,
              currentValue: pheicDeclarationDay > 0 ? `declared day ${pheicDeclarationDay}` : 'not declared',
              threshold: 'declared',
              pctToTrigger: pheicDeclarationDay > 0 ? 100 : 0,
              fireDay: pheicDeclarationDay > 0 && pheicDeclarationDay <= horizonDays ? pheicDeclarationDay : null,
            };
          }
          case 'days-elapsed': {
            return {
              label: c.label,
              currentValue: '0d',
              threshold: `${c.threshold}d`,
              pctToTrigger: Math.min(100, (0 / c.threshold) * 100),
              fireDay: c.threshold <= horizonDays ? c.threshold : null,
            };
          }
          case 'multilateral-request': {
            // Modelled as fired ~14d after PHEIC declaration (typical
            // member-state-request lag observed for past WB grant cycles).
            const fireDay = pheicDeclarationDay > 0 ? pheicDeclarationDay + 14 : null;
            return {
              label: c.label,
              currentValue: fireDay !== null ? `expected day ${fireDay}` : 'not requested',
              threshold: 'request received',
              pctToTrigger: fireDay !== null ? 100 : 0,
              fireDay,
            };
          }
        }
      });

      // AND logic: instrument fires when ALL criteria have fired.
      const allFireDays = criteriaResults.map(c => c.fireDay);
      const triggerFireDay = allFireDays.some(d => d === null)
        ? null
        : Math.max(...allFireDays.map(d => d as number));

      return {
        instrument: inst,
        criteriaResults,
        triggerFireDay,
        daysFromTriggerToPayout: inst.postTriggerDelayDays,
        regionEligible,
        ineligibilityReason,
      };
    });

    const peakInfectedDay = (() => {
      let maxI = 0, maxDay = 0;
      for (let t = 0; t < I.length; t++) {
        if (I[t]! > maxI) { maxI = I[t]!; maxDay = t; }
      }
      return { maxI, maxDay };
    })();

    return {
      pathogen, region, spreadClass, spreadCurve,
      D, I, instrumentResults,
      peakInfectedDay,
    };
  }, [pathogenId, regionId, horizonDays, pheicDeclarationDay]);

  if (!view) return <div>Loading…</div>;

  const sortedRegions = regions.slice().sort((a, b) => a.names.english.localeCompare(b.names.english));

  return (
    <SurfaceScaffold
      section="§7.34"
      title="Pandemic insurance trigger forecaster"
      subtitle={
        <>
          Parametric trigger projection · WB PEF / Pandemic Fund / ARC / re-insurance cat bonds ·
          days-to-trigger + payout-arrival · {view.region.names.english} · {view.pathogen.names.common}
        </>
      }
      honestyRail={
        <HonestyRail variant="limitation" label="NOT A SUBSTITUTE">
          The World Bank PEF (2017-2020) is widely criticised for COVID-19. Class B paid ~USD 95m
          in <strong>April 2020 — nine weeks AFTER the PHEIC declaration</strong>, far too late for
          the surge fiscal-capacity gap the instrument was supposed to fill (Stein &amp; Sridhar 2017
          Lancet, PMID 28527693; Brim &amp; Wenham 2019 BMJ Glob Health; Glassman 2020 CGD).
          Parametric instruments are calibrated for fast-burn outbreaks (Ebola). Slow-burn
          respiratory pandemics cross the death + spread thresholds AFTER the optimal
          pre-positioning window. <strong>Pre-position fiscal capacity assuming insurance will
          arrive late or not at all</strong> — unconditional facilities (expanded IMF SDR
          allocation; central-bank lender-of-last-resort; pre-committed reserve drawdown) are the
          binding constraint, not parametric payout.
        </HonestyRail>
      }
      kpis={
        <div className="kpis">
          {view.instrumentResults.slice(0, 4).map(r => (
            <div className="kpi" key={r.instrument.id}>
              <div className="kpi__label">{r.instrument.shortLabel}</div>
              <div className={`kpi__value ${!r.regionEligible ? '' : r.triggerFireDay !== null ? 'kpi__value--danger' : 'kpi__value--accent'}`}>
                {!r.regionEligible
                  ? 'N/A'
                  : r.triggerFireDay !== null
                    ? `Day ${r.triggerFireDay}`
                    : 'NOT FIRED'}
              </div>
              <div className="kpi__sub">
                {!r.regionEligible
                  ? 'region not eligible'
                  : r.triggerFireDay !== null
                    ? `payout ~day ${r.triggerFireDay + r.daysFromTriggerToPayout} (+${r.daysFromTriggerToPayout}d post-trigger)`
                    : 'no trigger in horizon'}
              </div>
            </div>
          ))}
        </div>
      }
      inputs={
        <div className="panel">
          <div className="panel__head"><span className="panel__title">Scenario</span></div>
          <div className="simctl">
            <label className="simctl__field">
              <span className="simctl__label">Pathogen</span>
              <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
                {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
              </select>
            </label>
            <label className="simctl__field">
              <span className="simctl__label">Region</span>
              <select value={regionId} onChange={e => setRegionId(e.target.value)}>
                {sortedRegions.map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
              </select>
            </label>
            <label className="simctl__field">
              <span className="simctl__label">PHEIC declaration day · {pheicDeclarationDay > 0 ? `day ${pheicDeclarationDay}` : 'not declared'}</span>
              <input type="range" min="0" max="180" step="1" value={pheicDeclarationDay}
                onChange={e => setPheicDeclarationDay(parseInt(e.target.value, 10))} />
              <span style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', marginTop: 4 }}>
                Sync with §7.26 PHEIC Predictor. 0 = never declared. Median of past 10 declared PHEICs ≈ 60d.
              </span>
            </label>
          </div>
          <div style={{ padding: '8px 14px', fontSize: 11.5, color: 'var(--bm-text-subtle)' }}>
            Spread class: <strong>{view.spreadClass}</strong> · curve anchor: {view.spreadCurve.source}
          </div>
        </div>
      }
    >
      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Cumulative deaths · drives mortality-threshold triggers</span>
          <span className="panel__meta">SEIRD D-compartment · {view.region.names.english}</span>
        </div>
        <Sparkline values={view.D} label="Cumulative deaths" stroke="var(--bm-danger)" interactive />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Instrument-by-instrument trigger ladder</span>
          <span className="panel__meta">criteria use AND logic — instrument fires when ALL fire</span>
        </div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Instrument</th>
              <th>Trigger criteria (ALL must fire)</th>
              <th className="num">Instrument fires</th>
              <th className="num">Payout arrives</th>
              <th>Payout magnitude</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {view.instrumentResults.map(r => (
              <tr key={r.instrument.id}>
                <td>
                  <strong>{r.instrument.shortLabel}</strong>
                  <br />
                  <span style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>{r.instrument.fullLabel}</span>
                </td>
                <td style={{ fontSize: 11.5 }}>
                  {r.criteriaResults.map((c, idx) => (
                    <div key={idx} style={{ marginBottom: 4 }}>
                      <span style={{
                        display: 'inline-block', width: 14, height: 14, marginRight: 6,
                        background: c.fireDay !== null ? 'var(--bm-accent, #6ea3ff)' : 'var(--bm-border)',
                        border: '1px solid var(--bm-border)', borderRadius: 2,
                        verticalAlign: 'middle',
                      }} title={c.fireDay !== null ? `Fires day ${c.fireDay}` : 'Does not fire in horizon'} />
                      <span style={{ color: 'var(--bm-text-muted)' }}>{c.label}</span>
                      <span style={{ marginLeft: 6, fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>
                        — threshold {c.threshold}; current {c.currentValue}
                        {c.fireDay !== null ? ` → day ${c.fireDay}` : ' → never'}
                      </span>
                    </div>
                  ))}
                </td>
                <td className="num" style={{ fontWeight: 600, color: !r.regionEligible ? 'var(--bm-text-subtle)' : r.triggerFireDay !== null ? 'var(--bm-danger)' : 'var(--bm-accent)' }}>
                  {!r.regionEligible
                    ? 'N/A'
                    : r.triggerFireDay !== null
                      ? `Day ${r.triggerFireDay}`
                      : 'NOT FIRED'}
                </td>
                <td className="num" style={{ fontWeight: 600, color: !r.regionEligible ? 'var(--bm-text-subtle)' : 'var(--bm-text)' }}>
                  {!r.regionEligible
                    ? '—'
                    : r.triggerFireDay !== null
                      ? `Day ${r.triggerFireDay + r.daysFromTriggerToPayout}`
                      : '—'}
                  <br />
                  <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--bm-text-subtle)' }}>
                    +{r.daysFromTriggerToPayout}d post-trigger delay
                  </span>
                </td>
                <td style={{ fontSize: 11.5, color: 'var(--bm-text-muted)' }}>
                  <strong>{r.instrument.payoutLabel}</strong>
                  <br />
                  <span style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>{r.instrument.payoutAnchor}</span>
                </td>
                <td style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)' }}>
                  {r.instrument.source}
                  {r.ineligibilityReason && (
                    <>
                      <br />
                      <span style={{ color: 'var(--bm-warn, #d4a857)' }}>{r.ineligibilityReason}</span>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Pin to minister briefing</span>
        </div>
        <div style={{ padding: '12px 14px', fontSize: 12.5, lineHeight: 1.55, color: 'var(--bm-text-muted)' }}>
          <PinToBriefButton item={{
            id: `pandemic-insurance-${pathogenId}-${regionId}-pheic${pheicDeclarationDay}`,
            surface: '§7.34',
            label: `Insurance triggers · ${view.region.names.english} · ${view.pathogen.names.common} · ${view.instrumentResults.filter(r => r.triggerFireDay !== null && r.regionEligible).length}/${view.instrumentResults.length} instruments fire`,
            value: view.instrumentResults.find(r => r.instrument.id === 'wb-pef-class-a')?.triggerFireDay ?? -1,
            context: {
              pathogenId,
              regionId,
              pheicDeclarationDay,
              spreadClass: view.spreadClass,
              instruments: view.instrumentResults.map(r => ({
                id: r.instrument.id,
                triggerFireDay: r.triggerFireDay,
                payoutDay: r.triggerFireDay !== null ? r.triggerFireDay + r.daysFromTriggerToPayout : null,
                payoutLabel: r.instrument.payoutLabel,
                regionEligible: r.regionEligible,
              })),
              honestyRail: 'Parametric insurance arrives WEEKS after trigger. NOT a substitute for unconditional surge facilities.',
            },
          }} />
          <span style={{ marginLeft: 10, fontSize: 11.5 }}>
            Finance-Minister persona briefing surfaces the trigger forecast alongside the
            "insurance-is-not-surge-capacity" rail — pre-position fiscal capacity assuming
            parametric instruments arrive late or not at all.
          </span>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Tracked limitations</span></div>
        <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--bm-text-muted)', font: '12px var(--bm-font-mono)' }}>
          <li>PEF 2017-20 EXPIRED in 2020. Class A + Class B modelled as reference instruments to expose the structural pattern; not currently issued. Pandemic Fund (2022+) is the successor but uses request-based grants, NOT parametric triggers.</li>
          <li>Private re-insurance / cat-bond market triggers (Munich Re / Swiss Re / SCOR syndicated) are mostly UNDISCLOSED — only the IBRD 2017 issuance has fully public criteria. The "reinsurance cat-bond reference" row is calibrated to that one public anchor.</li>
          <li>Country-spread curve is empirical, not simulated — uses observed COVID-2020 / SARS-2003 / H1N1-2009 / Ebola-2014 trajectories bucketed by R0 + infectious-period class. Real cross-border spread depends on aviation flow + restriction tier (see §7.33).</li>
          <li>PHEIC declaration day is operator-set, not predicted by this surface (cross-reference §7.26 PHEIC Predictor). The two surfaces should be reviewed together.</li>
          <li>ARC Outbreak insurance country eligibility list is the AU member states (54); pool participation is opt-in and may not cover every member at any given time.</li>
          <li>Death + spread thresholds use the regional SEIRD result — not a global aggregate. Country-spread treats spread as an empirical curve independent of region choice. A future iteration should run multi-region SEIRD and aggregate cross-border death + country counts.</li>
        </ul>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Feedback-loop mechanism · how this surface plugs into decision support</span></div>
        <ol style={{ padding: '12px 14px 12px 32px', margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--bm-text-muted)' }}>
          <li><strong>§7.4 Scenario Simulator</strong> provides the underlying SEIRD curve; this surface convolves the D-compartment with parametric trigger criteria.</li>
          <li><strong>§7.26 PHEIC Predictor</strong> projects WHO declaration probability + day; this surface consumes the day as an input (PHEIC is a trigger for the Pandemic Fund + cat-bond instruments).</li>
          <li><strong>§7.19 Cost-per-DALY</strong> finance-side framing pairs with this surface: cost-per-DALY estimates the demand for fiscal capacity; this surface estimates when parametric supply arrives.</li>
          <li><strong>§7.25 Minister Briefing Pack</strong> Finance-persona pack auto-includes the trigger forecast + the "insurance-is-not-surge-capacity" honesty rail.</li>
          <li><strong>§7.21 After-Action Review</strong> retrospects realised payout date vs forecast — calibration loop on the post-trigger delay days.</li>
        </ol>
      </div>
    </SurfaceScaffold>
  );
}
